<template>
  <InputGroup class="clearable-input-mask">
    <RygenInputMask
      :id="id"
      ref="inputMask"
      v-model="internalValue"
      :auto-clear="false"
      :disabled="!!disabled"
      :mask="mask"
      class="w-full"
    />

    <InputGroupAddon v-if="internalValue">
      <Button
        :aria-label="t('common.clearParam', { param: label })"
        :disabled="!!disabled"
        icon="pi pi-times"
        tabindex="-1"
        @click="onClear"
      />
    </InputGroupAddon>
  </InputGroup>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import { nextTick, toRefs, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';

import RygenInputMask from '@rygen/rygen-ui/components/form/RygenInputMask.vue';
import Button from 'primevue/button';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';

const props = defineProps({
  modelValue: {
    type: String,
    default: () => '',
  },
  id: {
    type: String,
    required: true,
  },
  mask: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: () => '',
  },
  disabled: {
    // This lets consumers pass anything to `disabled`, and we'll !! it to
    // convert it to a boolean
    type: [Boolean, String, Number, Array, Object],
    default: () => false,
  },
});
const { modelValue, id, mask, label, disabled } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: string): void;
}>();

const inputMask = useTemplateRef('inputMask');

const { internalValue } = useModelValue(modelValue as Ref<string | undefined>, {
  emit,
  factoryMethod: (newVal: string | undefined) => newVal,
});
const { t } = useI18n();

const onClear = () => {
  internalValue.value = undefined;
  const input = inputMask.value?.$el;
  nextTick(() => input?.focus());
};
</script>
